import React, { useEffect, useState } from 'react';
import { userServices } from '../../APIServices/userServices';
import { useToast } from '../../hooks/useToast';

function TwofactorAuth({ activeItem }) {
  const toast = useToast();
  const [copiedField, setCopiedField] = useState(false);
  const [authType, setAuthType] = useState('apps');
  const [status, setStatus] = useState(false);
  const [appTwoFA, setAppTwoFA] = useState(false);
  const [emailTwoFA, setEmailTwoFA] = useState(false);
  const [loader, setLoader] = useState(false);
  const [updateAuthType, setUpdateAuthType] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpSent, setOtpSent] = useState(false);
  const [base64, setBase64] = useState('');
  const [secret, setSecret] = useState('');
  const [check, setCheck] = useState(false);
  const [disable, setDisable] = useState(false);
  const [showQR, setShowQR] = useState(false);

  const OTP_LENGTH = 6;

  const getTwoFactorAuthType = async () => {
    setLoader(true);
    try {
      const data = await userServices.getTwoFactorAuthType();
      setAuthType(data?.data?.twoFactorAuthType || 'apps');
      setStatus(data?.data?.status || false);
      setAppTwoFA(data?.data?.appTwoFA || false);
      setEmailTwoFA(data?.data?.emailTwoFA || false);
    } catch (error) {
      console.error(error);
    } finally {
      setLoader(false);
    }
  };

  const handleOtpBackspace = (e, index) => {
    if (e.key !== 'Backspace') return;
    const newOtp = [...otp];
    newOtp[index] = '';
    setOtp(newOtp);
    if (index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (pasted.length === OTP_LENGTH) {
      setOtp(pasted.split(''));
      document.getElementById(`otp-${OTP_LENGTH - 1}`)?.focus();
    }
  };

  const twoFactorAuthType = async () => {
    setLoader(true);
    try {
      const data = await userServices.twoFactorAuthType({ type: authType });
      if (data?.statusCode === 200) {
        getTwoFactorAuthType();
        setUpdateAuthType(true);
      }
    } catch (error) {
      toast('Unable to update 2FA type.', 'warn');
    } finally {
      setLoader(false);
    }
  };

  const generateQRCode = async () => {
    setLoader(true);
    try {
      const data = await userServices.generateQRCode();
      setBase64(data?.data || data?.base64 || '');
      setSecret(data?.secret || data?.data?.secret || '');
    } catch (error) {
      toast('Unable to generate QR code.', 'warn');
    } finally {
      setLoader(false);
    }
  };

  const showQRCode = async () => {
    setLoader(true);
    try {
      const data = await userServices.showQRCode();
      setBase64(data?.data || data?.base64 || '');
      setSecret(data?.secret || data?.data?.secret || '');
      if (data) {
        setShowQR(true);
      }
    } catch (error) {
      toast('Unable to load QR code.', 'warn');
    } finally {
      setLoader(false);
    }
  };

  const checkQRCode = async () => {
    try {
      await userServices.checkQRCode({ secret, otp: otp.join('') });
      toast('QR code verified.', 'ok');
    } catch (error) {
      toast('OTP verification failed.', 'warn');
    }
  };

  const enableTwoFactorAuth = async () => {
    try {
      const data = await userServices.enableTwoFactorAuth({ secret, otp: otp.join('') });
      if (data?.statusCode === 200) {
        getTwoFactorAuthType();
        setOtp(['', '', '', '', '', '']);
      }
    } catch (error) {
      toast('Unable to enable app 2FA.', 'warn');
    }
  };

  const disableTwoFactorAuth = async () => {
    try {
      const data = await userServices.disableTwoFactorAuth({ secret, otp: otp.join('') });
      if (data?.statusCode === 200) {
        getTwoFactorAuthType();
        setDisable(false);
        setShowQR(false);
        setCheck(false);
        setBase64('');
        setSecret('');
        setOtp(['', '', '', '', '', '']);
      }
    } catch (error) {
      toast('Unable to disable app 2FA.', 'warn');
    }
  };

  const sendOTPForEmailTwoFa = async () => {
    try {
      const data = await userServices.emailOTPFor2FA();
      if (data?.statusCode === 200) {
        setOtpSent(true);
      }
    } catch (error) {
      toast('Unable to send OTP.', 'warn');
    }
  };

  const enableTwoFactorAuthByEmail = async () => {
    try {
      const data = await userServices.enableTwoFactorAuthByEmail({ otp: otp.join('') });
      if (data?.statusCode === 200) {
        getTwoFactorAuthType();
        setOtp(['', '', '', '', '', '']);
        setOtpSent(false);
      }
    } catch (error) {
      toast('Unable to enable email 2FA.', 'warn');
    }
  };

  const disableTwoFactorAuthByEmail = async () => {
    try {
      const data = await userServices.disableTwoFactorAuthByEmail({ otp: otp.join('') });
      if (data?.statusCode === 200) {
        getTwoFactorAuthType();
        setOtpSent(false);
        setOtp(['', '', '', '', '', '']);
      }
    } catch (error) {
      toast('Unable to disable email 2FA.', 'warn');
    }
  };

  const getQRDataForDisable = async () => {
    await showQRCode();
    setDisable(true);
  };

  useEffect(() => {
    getTwoFactorAuthType();
  }, [activeItem]);

  const handleCopy = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(true);
    setTimeout(() => setCopiedField(false), 1500);
  };

  const handleOtpInput = (value, index) => {
    const digit = value.replace(/\D/, '');
    if (!digit) return;
    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);
    if (index < OTP_LENGTH - 1) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  const renderOtpInputs = () => (
    <div className="hstack" style={{ justifyContent: 'center', gap: 10, flexWrap: 'wrap' }}>
      {otp.map((digit, index) => (
        <input
          key={index}
          id={`otp-${index}`}
          type="text"
          maxLength={1}
          inputMode="numeric"
          value={digit}
          className="inp text-center"
          style={{ width: 42 }}
          onChange={(e) => handleOtpInput(e.target.value, index)}
          onPaste={handleOtpPaste}
          onKeyDown={(e) => handleOtpBackspace(e, index)}
        />
      ))}
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Manage Secure Account</div>
          <h1 className="page-title">Two Factor Authentication</h1>
          <p className="page-sub">
            Strengthen your account security by enabling two-factor authentication (2FA). Add an extra layer of protection by requiring a verification code during sign-in.
          </p>
        </div>
      </div>

      <div className="flex between center mb-3">
        <div className="section-label">Two Factor Authentication</div>
      </div>

      <div className="card card-pad">
        <div className="field">
          <label className="field-label">Current Status</label>
          <div className={`tag ${status ? 'tag-ok' : 'tag-danger'}`}>
            {status ? 'Enabled' : 'Disabled'}
          </div>
        </div>

        {!updateAuthType ? (
          <>
            <div className="field">
              <label className="field-label">Authentication Type</label>
              <select
                className="inp"
                onChange={(e) => setAuthType(e.target.value)}
                value={authType}
                disabled={loader}
              >
                <option value="apps">By Apps</option>
                <option value="email">By Email</option>
              </select>
            </div>
            <div className="flex between center" style={{ gap: 12 }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={twoFactorAuthType}
                disabled={loader}
              >
                Next
              </button>
            </div>
          </>
        ) : (
          <>
            {authType === 'apps' ? (
              <>
                {!disable ? (
                  <>
                    {!appTwoFA ? (
                      <>
                        {!check ? (
                          <>
                            <div className="field text-center">
                              {base64 ? (
                                <>
                                  <div className="card card-pad" style={{ maxWidth: 180, margin: '0 auto' }}>
                                    <img src={base64} alt="QR" style={{ width: '100%' }} />
                                  </div>
                                  <div className="hstack" style={{ justifyContent: 'center', gap: 10, marginTop: 12 }}>
                                    <span>{secret}</span>
                                    {secret && (
                                      <button
                                        type="button"
                                        className="btn btn-ghost btn-sm"
                                        onClick={() => handleCopy(secret)}
                                      >
                                        {copiedField ? 'Copied' : 'Copy'}
                                      </button>
                                    )}
                                  </div>
                                </>
                              ) : (
                                <div className="tag tag-danger" style={{ width: 180, margin: '0 auto' }}>
                                  QR not generated yet
                                </div>
                              )}
                            </div>

                            <div className="flex between center" style={{ gap: 12, flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={() => {
                                  setUpdateAuthType(false);
                                  setBase64('');
                                  setSecret('');
                                }}
                              >
                                Back
                              </button>
                              <button type="button" className="btn btn-primary" onClick={generateQRCode}>
                                {base64 ? 'Re-Generate QR' : 'Generate QR'}
                              </button>
                              {base64 && (
                                <button type="button" className="btn btn-primary" onClick={() => setCheck(true)}>
                                  Next
                                </button>
                              )}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="field text-center">
                              <label className="field-label">Verification Code</label>
                              {renderOtpInputs()}
                            </div>
                            <div className="flex between center" style={{ gap: 12, flexWrap: 'wrap' }}>
                              <button type="button" className="btn btn-ghost" onClick={() => setCheck(false)}>
                                Back
                              </button>
                              <button type="button" className="btn btn-primary" onClick={checkQRCode}>
                                Verify
                              </button>
                              <button type="button" className="btn btn-primary" onClick={enableTwoFactorAuth}>
                                Verify and Enable
                              </button>
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {!showQR ? (
                          <>
                            <div className="field text-center">
                              <div className="tag tag-ok" style={{ display: 'inline-flex', justifyContent: 'center' }}>
                                Enabled (2FA by Apps)
                              </div>
                            </div>
                            <div className="flex between center" style={{ gap: 12, flexWrap: 'wrap' }}>
                              <button type="button" className="btn btn-primary" onClick={showQRCode}>
                                Show QR
                              </button>
                              <button type="button" className="btn btn-danger" onClick={getQRDataForDisable}>
                                Disable
                              </button>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="field text-center">
                              {base64 && (
                                <>
                                  <div className="card card-pad" style={{ maxWidth: 180, margin: '0 auto' }}>
                                    <img src={base64} alt="QR" style={{ width: '100%' }} />
                                  </div>
                                  <div className="hstack" style={{ justifyContent: 'center', gap: 10, marginTop: 12 }}>
                                    <span>{secret}</span>
                                    {secret && (
                                      <button
                                        type="button"
                                        className="btn btn-ghost btn-sm"
                                        onClick={() => handleCopy(secret)}
                                      >
                                        {copiedField ? 'Copied' : 'Copy'}
                                      </button>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                            <div className="flex between center" style={{ gap: 12, flexWrap: 'wrap' }}>
                              <button type="button" className="btn btn-ghost" onClick={() => setShowQR(false)}>
                                Hide QR
                              </button>
                              <button type="button" className="btn btn-danger" onClick={getQRDataForDisable}>
                                Disable
                              </button>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <div className="field text-center">
                      <label className="field-label">Verification Code</label>
                      {renderOtpInputs()}
                    </div>
                    <div className="flex between center" style={{ gap: 12, flexWrap: 'wrap' }}>
                      <button type="button" className="btn btn-ghost" onClick={() => {
                        setDisable(false);
                        setOtp(['', '', '', '', '', '']);
                      }}>
                        Back
                      </button>
                      <button type="button" className="btn btn-danger" onClick={disableTwoFactorAuth}>
                        Disable 2FA
                      </button>
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                {!emailTwoFA ? (
                  <>
                    {!otpSent ? (
                      <>
                        <div className="field text-center">
                          <div className="tag tag-danger" style={{ display: 'inline-flex', justifyContent: 'center' }}>
                            Disabled
                          </div>
                          <p className="page-sub" style={{ marginTop: 12 }}>
                            To enable Email 2FA, send OTP to your registered email address.
                          </p>
                        </div>
                        <div className="flex between center" style={{ gap: 12, flexWrap: 'wrap' }}>
                          <button type="button" className="btn btn-ghost" onClick={() => setUpdateAuthType(false)}>
                            Back
                          </button>
                          <button type="button" className="btn btn-primary" onClick={sendOTPForEmailTwoFa}>
                            Send OTP
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="field text-center">
                          <label className="field-label">Verification Code</label>
                          {renderOtpInputs()}
                        </div>
                        <div className="flex between center" style={{ gap: 12, flexWrap: 'wrap' }}>
                          <button type="button" className="btn btn-ghost" onClick={() => setOtpSent(false)}>
                            Back
                          </button>
                          <button type="button" className="btn btn-primary" onClick={enableTwoFactorAuthByEmail}>
                            Verify with Enable
                          </button>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {!otpSent ? (
                      <>
                        <div className="field text-center">
                          <div className="tag tag-ok" style={{ display: 'inline-flex', justifyContent: 'center' }}>
                            Enabled (2FA by Email)
                          </div>
                        </div>
                        <div className="flex between center" style={{ gap: 12, flexWrap: 'wrap' }}>
                          <button type="button" className="btn btn-danger" onClick={sendOTPForEmailTwoFa}>
                            Disable
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="field text-center">
                          <label className="field-label">Verification Code</label>
                          {renderOtpInputs()}
                        </div>
                        <div className="flex between center" style={{ gap: 12, flexWrap: 'wrap' }}>
                          <button type="button" className="btn btn-ghost" onClick={() => setOtpSent(false)}>
                            Back
                          </button>
                          <button type="button" className="btn btn-danger" onClick={disableTwoFactorAuthByEmail}>
                            Disable 2FA
                          </button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default TwofactorAuth;
